import React from 'react';
import { titleize } from 'underscore.string';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@cargomatic/ui';
import { CargomaticDropzone, DROPZONE_VARIANTS } from 'components/v2/Dropzone';
import { ACCEPTED_FILE_TYPES } from 'config/accepted-file-types';
import { isPDF } from 'utils/documents';
import { getDocumentTypes, uploadDocuments } from 'utils/DocumentsAPIUtils';
import { CUSTOM, carrierPortalDocTypes } from 'config/documents';
import PDFViewer from 'components/modules/shipments/PDFViewer';
import CargomaticSelect from 'components/v2/select';
import CargomaticInput from 'components/v2/input';
import { showToast } from 'stores/ToastStore';
import AdminServerActionCreators from 'actions/AdminServerActionCreators';

/* Modal With Dropzone and File Previews for Document Uploads */
export class DocumentUploadModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      type: '',
      files: [],
      customTitle: '',
      types: [],
    };
  }

  componentDidMount() {
    // commenting out to bring back when we reimplement doc service docTypes API, see function below.
    // this.fetchDocumentTypes();
    this.setState({ types: carrierPortalDocTypes });
  }

  // Leaving until phase two we can reinstate
  fetchDocumentTypes = async () => {
    try {
      const { data, errors } = await getDocumentTypes('shipment');

      if (errors && errors.length) {
        this.showGetDocumentTypesErrorToast(errors.join('\n'));
      }

      if (data) {
        const { getAvailableDocumentTypes } = data;
        const filteredTypes = getAvailableDocumentTypes.filter((type) =>
          Boolean(type.canUpload),
        );

        this.setState({ types: filteredTypes });
      }
    } catch (error) {
      console.error('Error fetching document types:', error);
    }
  };

  showGetDocumentTypesErrorToast = (error) => {
    const { t } = this.props;

    showToast(
      t('GET_DOCUMENT_TYPES_ERROR'),
      t('GET_DOCUMENT_FAILED_WITH_ERROR', { error }),
      'error',
    );
  };

  handleDocumentTypeSelect = (item) => {
    const { value: type } = item;
    if (type !== CUSTOM.value) {
      this.setState({ customTitle: '' });
    }

    this.setState({ type });
  };

  renderSelection() {
    const { t } = this.props;
    const { types } = this.state;
    const { type } = this.state;

    if (types?.length) {
      return (
        <CargomaticSelect
          label={t('DOCUMENT_TYPE')}
          items={types}
          onSelect={this.handleDocumentTypeSelect}
          caption={t('PLEASE_SELECT_A_DOCUMENT_TYPE')}
          valueKey="value"
          textKey="localizationSlug"
          selected={type}
          showCaption
        />
      );
    } else {
      return <Skeleton className="h-10 w-full" />;
    }
  }

  handleCustomTitleChange = (title) => {
    this.setState({ customTitle: title });
  };

  renderPreview() {
    if (!this.state.files.length) {
      return null;
    }
    const filePreviews = this.state.files.map((file, index) => {
      const fileType = file.type;
      if (isPDF(fileType)) {
        return (
          <div key={index}>
            <PDFViewer file={file.preview} />
          </div>
        );
      }
      return (
        <div key={index}>
          <img className="w-full h-auto" src={file.preview} alt="Preview" />
        </div>
      );
    });

    return (
      <div>
        {filePreviews}
        <hr />
      </div>
    );
  }

  upload = async () => {
    this.props.onHide();

    try {
      const { t } = this.props;
      const { types, type } = this.state;
      const documentName =
        this.state.type === CUSTOM.value
          ? this.state.customTitle
          : this.state.type;

      const documentType = types.find((typeItem) => typeItem.value === type);

      const data = this.state.files.map((file) => {
        return {
          type: documentName,
          content: file,
          contentType: file.type,
          name: documentType?.name,
          size: file.size,
          foreignKeys: [this.props.shipment._id],
        };
      });

      const { error: uploadError } = await uploadDocuments(data);

      if (uploadError) {
        const standardErrorSlug = t('DOCUMENT_UPLOAD_FAILED_WITH_ERROR', {
          uploadError,
        });
        const limitErrorSlug = t('FILE_SIZE_TOO_BIG', {
          MB: uploadError._limit,
        });
        const slugError = uploadError._limit
          ? limitErrorSlug
          : standardErrorSlug;

        showToast(t('DOCUMENT_UPLOAD_FAILED_RETRY'), slugError, 'error');
      } else {
        AdminServerActionCreators.receiveShipmentUpdate(this.props.shipment);

        this.props.onUpload();
      }
    } catch (e) {
      console.error(e);
    }
  };

  handleFile = (file) => {
    this.setState((previousState) => {
      return {
        files: [...previousState.files, ...file],
      };
    });
  };

  renderUploader() {
    const { t } = this.props;
    return (
      <div>
        <div>{this.state.files.length ? this.renderPreview() : null}</div>
        <CargomaticDropzone
          variant={DROPZONE_VARIANTS.BORDERED}
          onDrop={this.handleFile}
          multiple
          accept={ACCEPTED_FILE_TYPES}
        >
          <div>{t('CLICK_OR_DRAG_TO_UPLOAD_FILE')}</div>
        </CargomaticDropzone>
      </div>
    );
  }

  saveDisabled() {
    if (!this.state.files.length) {
      return true;
    }

    if (!this.state.type) {
      return true;
    }

    if (this.state.type === CUSTOM.value && !this.state.customTitle) {
      return true;
    }

    return false;
  }

  render() {
    const { t } = this.props;
    return (
      <Dialog
        open={this.props.show}
        onOpenChange={(open) => {
          if (!open) this.props.onHide();
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t('UPLOAD_ADDITIONAL_DOCUMENTS_SHIPMENT', {
                shipmentId: this.props.shipment.shipment_id,
              })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="overflow-hidden">{this.renderUploader()}</div>
            <div className="grid grid-cols-2 gap-4">
              {this.renderSelection()}
              {this.state.type === CUSTOM.value && (
                <CargomaticInput
                  label={t('CUSTOM_TITLE')}
                  placeholder={t('ENTER_DOCUMENT_TITLE')}
                  onChange={this.handleCustomTitleChange}
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={this.upload} disabled={this.saveDisabled()}>
              {titleize(t('UPLOAD'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
}
